'use strict';

(() => {
  const isAddToCartForm = (form) =>
    form instanceof HTMLFormElement && form.getAttribute('data-type') === 'add-to-cart-form';

  const getAddToCartForm = (host) => {
    if (!(host instanceof HTMLElement)) return null;

    const closestForm = host.closest('form[data-type="add-to-cart-form"]');
    if (isAddToCartForm(closestForm)) return closestForm;

    const parent = host.closest('product-info-component');
    if (!parent) return null;

    const scoped = parent.querySelector('form[data-type="add-to-cart-form"]');
    return isAddToCartForm(scoped) ? scoped : null;
  };

  const getQuantity = (input) => {
    const val = Number.parseInt(input?.value, 10);
    return Number.isFinite(val) && val > 0 ? val : 1;
  };

  const getMin = (input) => {
    const val = Number.parseInt(input?.min, 10);
    return Number.isFinite(val) && val > 0 ? val : 1;
  };

  const linkInputToForm = (host, input) => {
    if (!(input instanceof HTMLInputElement)) return;
    if (input.name !== 'quantity' || input.hasAttribute('form')) return;

    const form = getAddToCartForm(host);
    if (!isAddToCartForm(form) || !form.id) return;
    if (form.contains(input)) return;

    input.setAttribute('form', form.id);
  };

  class QuantitySelectorComponent extends HTMLElement {
    constructor() {
      super();
      this.handleDecrement = this.handleDecrement.bind(this);
      this.handleIncrement = this.handleIncrement.bind(this);
    }

    connectedCallback() {
      this.decrementBtn = this.querySelector('[data-qty-decrement], button[name="minus"]');
      this.incrementBtn = this.querySelector('[data-qty-increment], button[name="plus"]');
      this.quantityInput = this.querySelector('[data-qty-input], input[name="quantity"]');

      if (!this.decrementBtn || !this.incrementBtn || !this.quantityInput) return;

      linkInputToForm(this, this.quantityInput); // no-op when input is already inside the cart form

      this.setupAriaLiveRegion();

      this.decrementBtn.addEventListener('click', this.handleDecrement);
      this.incrementBtn.addEventListener('click', this.handleIncrement);

      this.updateDecrementState();
    }

    disconnectedCallback() {
      this.decrementBtn?.removeEventListener('click', this.handleDecrement);
      this.incrementBtn?.removeEventListener('click', this.handleIncrement);

      this.decrementBtn = null;
      this.incrementBtn = null;
      this.quantityInput = null;
      this.ariaLiveEl = null;
    }

    setupAriaLiveRegion() {
      if (this.ariaLiveEl && this.contains(this.ariaLiveEl)) return;

      let ariaLive = this.querySelector('.quantity-aria-live-region');

      if (!ariaLive) {
        ariaLive = document.createElement('div');
        ariaLive.className = 'screen-reader-only quantity-aria-live-region';
        ariaLive.setAttribute('aria-live', 'assertive');
        ariaLive.setAttribute('aria-atomic', 'true');
        this.appendChild(ariaLive);
      }

      this.ariaLiveEl = ariaLive;
    }

    announce(message) {
      const ariaLive = this.ariaLiveEl;
      if (!ariaLive) return;

      ariaLive.textContent = '';
      setTimeout(() => {
        ariaLive.textContent = message;
      }, 50);
    }

    updateDecrementState() {
      if (!this.decrementBtn || !this.quantityInput) return;

      const value = getQuantity(this.quantityInput);
      const min = getMin(this.quantityInput);

      this.decrementBtn.disabled = value <= min;
    }

    updateQuantity(newValue) {
      if (!this.quantityInput) return;

      this.quantityInput.value = String(newValue);
      this.updateDecrementState();

      const ariaLabel = this.quantityInput.getAttribute('aria-label') || '';
      const productTitle = ariaLabel.replace(/^quantity\s+for\s+/i, '').trim();

      if (productTitle) {
        this.announce(`${productTitle} quantity: ${newValue}`);
      } else {
        this.announce(`Quantity: ${newValue}`);
      }
    }

    handleDecrement() {
      const value = getQuantity(this.quantityInput);
      const min = getMin(this.quantityInput);

      if (value <= min) return;

      this.updateQuantity(value - 1);
    }

    handleIncrement() {
      const value = getQuantity(this.quantityInput);
      this.updateQuantity(value + 1);
    }
  }
  if (!customElements.get('quantity-selector-component')) {
    customElements.define('quantity-selector-component', QuantitySelectorComponent);
  }
})();
